import { FunctionComponent, useState, useCallback } from 'react';
import { DashboardWrapper, Text, Button, Card } from 'src/shared';
import styled from 'styled-components';
import { Colors } from 'src/utils/color';
import { Size } from 'src/shared/text';
import { ReactComponent as UploadIconSvg } from 'src/icons/upload.svg';
import { ReactComponent as ClipboardIconSvg } from 'src/icons/reading-mode.svg';
import { ReactComponent as WifiIconSvg } from 'src/icons/wifi.svg';
import { ReactComponent as CameraIconSvg } from 'src/icons/camera.svg';
import { ReactComponent as CheckIconSvg } from 'src/icons/star.svg';
import { ReactComponent as CloseIconSvg } from 'src/icons/close.svg';
import { ReactComponent as FolderIconSvg } from 'src/icons/storage.svg';
import { ReactComponent as EyeIconSvg } from 'src/icons/eye-hidden.svg';

// Mock data from RetinaTrack
interface UploadFile {
  id: string;
  name: string;
  size: number;
  type: 'retinal_scan' | 'eye_movement_data' | 'metadata';
  eyeSide: 'OD' | 'OS' | 'OU';
  status: 'uploading' | 'completed' | 'failed';
  progress: number;
}

interface UploadSession {
  id: string;
  patientMRN: string;
  patientName: string;
  sessionDate: string;
  deviceType: string;
  files: UploadFile[];
  status: 'uploading' | 'processing' | 'completed' | 'failed';
  notes?: string;
}

const mockUploadSessions: UploadSession[] = [
  {
    id: '1',
    patientMRN: 'MRN-2024-001',
    patientName: 'Emily Johnson',
    sessionDate: '2024-01-15T09:30:00Z',
    deviceType: 'heidelberg_spectralis',
    status: 'completed',
    notes: 'Routine follow-up scan',
    files: [
      {
        id: '1',
        name: 'emily_johnson_OD_retinal_scan.dcm',
        size: 15728640,
        type: 'retinal_scan',
        eyeSide: 'OD',
        status: 'completed',
        progress: 100
      },
      {
        id: '2',
        name: 'emily_johnson_OS_retinal_scan.dcm',
        size: 16234496,
        type: 'retinal_scan',
        eyeSide: 'OS',
        status: 'completed',
        progress: 100
      },
      {
        id: '3',
        name: 'emily_johnson_fixation_data.csv',
        size: 524288,
        type: 'eye_movement_data',
        eyeSide: 'OU',
        status: 'completed',
        progress: 100
      }
    ]
  },
  {
    id: '2',
    patientMRN: 'MRN-2024-002',
    patientName: 'Robert Chen',
    sessionDate: '2024-01-14T14:20:00Z',
    deviceType: 'eyelink_1000',
    status: 'processing',
    notes: 'Neurological assessment - MS monitoring',
    files: [
      {
        id: '4',
        name: 'robert_chen_eye_tracking.edf',
        size: 8388608,
        type: 'eye_movement_data',
        eyeSide: 'OU',
        status: 'completed',
        progress: 100
      },
      {
        id: '5',
        name: 'robert_chen_OD_fundus.jpg',
        size: 2097152,
        type: 'retinal_scan',
        eyeSide: 'OD',
        status: 'uploading',
        progress: 65
      }
    ]
  }
];

const deviceTypes = [
  { value: 'heidelberg_spectralis', label: 'Heidelberg Spectralis OCT' },
  { value: 'zeiss_cirrus', label: 'Zeiss Cirrus HD-OCT' },
  { value: 'topcon_3d', label: 'Topcon 3D OCT-1 Maestro' },
  { value: 'eyelink_1000', label: 'SR Research EyeLink 1000' },
  { value: 'tobii_pro', label: 'Tobii Pro Spectrum' },
  { value: 'sensomotoric_eyelink', label: 'SMI EyeTracking Glasses' },
  { value: 'custom_device', label: 'Custom/Other Device' }
];

function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: '2-digit', 
    day: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

function getDeviceLabel(deviceType: string) {
  const device = deviceTypes.find(d => d.value === deviceType);
  return device ? device.label : deviceType;
}

function getStatusIcon(status: string) {
  switch(status) {
    case 'completed':
      return '✅';
    case 'processing':
      return '⏳';
    case 'uploading':
      return '⬆️';
    case 'failed':
      return '❌';
    default:
      return '⏳';
  }
}

function getFileTypeIcon(type: string) {
  switch(type) {
    case 'retinal_scan':
      return '👁️';
    case 'eye_movement_data':
      return '📹';
    case 'metadata':
      return '📄';
    default:
      return '📁';
  }
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 1px solid ${Colors.extra.light_gray};
  margin-bottom: 24px;
`;

const Tab = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: none;
  border: none;
  border-bottom: 2px solid ${p => p.active ? Colors.theme.primary : 'transparent'};
  color: ${p => p.active ? Colors.theme.primary : Colors.extra.dark_gray};
  font-size: 14px;
  font-weight: ${p => p.active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${Colors.theme.primary};
  }
`;

const TabIcon = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const FormCard = styled.div`
  background: ${Colors.extra.light_gray};
  border: 1px solid ${Colors.extra.light_gray};
  border-radius: 8px;
  padding: 24px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const CardIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 1;
  
  svg {
    width: 100%;
    height: 100%;
    display: block;
    vertical-align: middle;
  }
`;

const CardTitle = styled(Text)`
  font-size: ${Size.Medium};
  font-weight: 600;
  color: ${Colors.extra.black};
  line-height: 1.2;
  display: flex;
  align-items: center;
`;

const SessionInfoTitle = styled(CardTitle)`
  font-size: 18px;
`;

const FileUploadTitle = styled(CardTitle)`
  font-size: 18px;
`;

const CardSubtitle = styled(Text)`
  font-size: ${Size.Small};
  color: ${Colors.extra.dark_gray};
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${Colors.extra.black};
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${Colors.extra.light_gray};
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${Colors.theme.primary};
  }

  &::placeholder {
    color: ${Colors.extra.dark_gray};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid ${Colors.extra.light_gray};
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${Colors.theme.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${Colors.extra.light_gray};
  border-radius: 6px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${Colors.theme.primary};
  }

  &::placeholder {
    color: ${Colors.extra.dark_gray};
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  margin-top: 24px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
`;

const UploadZone = styled.div<{ isDragOver: boolean }>`
  border: 2px dashed ${props => props.isDragOver ? Colors.theme.primary : Colors.extra.light_gray};
  border-radius: 8px;
  padding: 60px 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props => props.isDragOver ? `${Colors.theme.primary}08` : 'transparent'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: ${Colors.theme.primary};
    background-color: ${Colors.theme.primary}08;
  }
`;

const UploadIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  color: ${Colors.theme.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const UploadTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${Colors.extra.black};
  margin-bottom: 8px;
`;

const UploadText = styled.div`
  font-size: 14px;
  color: ${Colors.extra.dark_gray};
  margin-bottom: 4px;
`;

const BrowseLink = styled.span`
  color: ${Colors.theme.primary};
  text-decoration: underline;
  cursor: pointer;
`;

const UploadFileInfo = styled.div`
  font-size: 12px;
  color: ${Colors.extra.dark_gray};
  margin-top: 12px;
`;

const SupportedTypes = styled.div`
  margin-top: 24px;
  padding: 16px;
  background-color: ${Colors.extra.light_gray}20;
  border-radius: 6px;
`;

const TypeSection = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TypeTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${Colors.extra.black};
  margin-bottom: 8px;
`;

const TypeList = styled.ul`
  font-size: 12px;
  color: ${Colors.extra.dark_gray};
  margin: 0;
  padding-left: 20px;
  line-height: 1.6;
`;

const Badge = styled.span`
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  background: ${Colors.theme.primary}15;
  color: ${Colors.theme.primary};
  border: 1px solid ${Colors.theme.primary}30;
`;

const SessionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SessionCard = styled(Card)`
  padding: 24px;
  border: 1px solid ${Colors.extra.light_gray};
  border-radius: 8px;
`;

const SessionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${Colors.extra.light_gray};
`;

const SessionHeaderLeft = styled.div`
  flex: 1;
`;

const SessionPatientName = styled(Text)`
  font-size: ${Size.Medium};
  font-weight: 600;
  color: ${Colors.extra.black};
  margin-bottom: 8px;
`;

const SessionDetails = styled(Text)`
  font-size: ${Size.Small};
  color: ${Colors.extra.dark_gray};
  margin-bottom: 8px;
`;

const SessionNotes = styled(Text)`
  font-size: ${Size.Small};
  color: ${Colors.extra.dark_gray};
  font-style: italic;
`;

const SessionStatus = styled.span<{ status: string }>`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  ${p => {
    switch(p.status) {
      case 'completed':
        return `background: #d1fae5; color: #065f46;`;
      case 'processing':
        return `background: #fef3c7; color: #92400e;`;
      case 'uploading':
        return `background: #dbeafe; color: #1e40af;`;
      case 'failed':
        return `background: #fee2e2; color: #b91c1c;`;
      default:
        return `background: ${Colors.extra.light_gray}40; color: ${Colors.extra.black};`;
    }
  }}
`;

const FilesSection = styled.div`
  margin-top: 16px;
`;

const FilesTitle = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${Colors.extra.black};
  margin-bottom: 12px;
`;

const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FileItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: ${Colors.extra.light_gray}20;
  border-radius: 6px;
  border: 1px solid ${Colors.extra.light_gray};
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const FileIcon = styled.span`
  font-size: 24px;
`;

const FileDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  color: ${Colors.extra.black};
  margin-bottom: 4px;
  word-break: break-word;
`;

const FileMeta = styled(Text)`
  font-size: 12px;
  color: ${Colors.extra.dark_gray};
`;

const FileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FileStatusIcon = styled.div<{ status: string }>`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const ActionButton = styled.button`
  padding: 6px 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 16px;
  border-radius: 4px;
  transition: background 0.2s ease;
  
  /* Make SVG icons inherit the button's text color */
  svg {
    fill: currentColor;
    stroke: currentColor;
  }

  &:hover {
    background: ${Colors.extra.light_gray}40;
  }
`;

const DeviceIntegrationHeader = styled.div`
  margin-bottom: 24px;
`;

const DevicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DeviceCard = styled(Card)`
  padding: 20px;
  border: 1px solid ${Colors.extra.light_gray};
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${Colors.theme.primary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const DeviceCardContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const DeviceIcon = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const DeviceInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DeviceName = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${Colors.extra.black};
  margin-bottom: 4px;
`;

const DeviceType = styled(Text)`
  font-size: 12px;
  color: ${Colors.extra.dark_gray};
`;

const ConfigureButton = styled(Button)`
  /* SVG icons will inherit color from Button component */
  padding: 8px 16px;
  font-size: 13px;
  flex-shrink: 0;
`;

const DirectIntegrationSection = styled.div`
  padding: 40px;
  background: ${Colors.extra.light_gray}20;
  border: 2px dashed ${Colors.extra.light_gray};
  border-radius: 12px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const DirectIntegrationIcon = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const DirectIntegrationTitle = styled(Text)`
  font-size: ${Size.Medium};
  font-weight: 600;
  color: ${Colors.extra.black};
`;

const DirectIntegrationDescription = styled(Text)`
  font-size: ${Size.Small};
  color: ${Colors.extra.dark_gray};
  max-width: 500px;
`;

const SetupButton = styled(Button)`
  /* SVG icons will inherit color from Button component */
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  margin-top: 8px;
`;

const Upload: FunctionComponent = () => {
  const [activeTab, setActiveTab] = useState<'new' | 'recent' | 'device'>('new');
  const [formData, setFormData] = useState({
    patientMRN: '',
    patientName: '',
    sessionDate: new Date().toISOString().split('T')[0],
    deviceType: '',
    notes: ''
  });
  const [files, setFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleInputChange = useCallback((field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleFileSelect = useCallback((selectedFiles: File[]) => {
    const validFiles = selectedFiles.filter(file => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      const validExtensions = ['jpg', 'jpeg', 'png', 'tiff', 'dcm', 'dicom', 'csv', 'edf', 'asc', 'txt', 'json'];
      return validExtensions.includes(extension || '');
    });
    setFiles(prev => [...prev, ...validFiles]);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFileSelect(droppedFiles);
  }, [handleFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    handleFileSelect(selectedFiles);
  }, [handleFileSelect]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.patientMRN || !formData.patientName || files.length === 0) {
      alert('Please fill in all required fields and select at least one file');
      return;
    }

    setUploading(true);
    try {
      // TODO: Implement actual upload logic
      // console.log('Creating upload session:', { formData, files });
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate upload
      alert('Upload session created successfully!');
      setFormData({
        patientMRN: '',
        patientName: '',
        sessionDate: new Date().toISOString().split('T')[0],
        deviceType: '',
        notes: ''
      });
      setFiles([]);
    } catch (error) {
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [formData, files]);

  return (
    <DashboardWrapper>
        <Container>
        <Header>
          <HeaderLeft>
            <Text fontSize={Size.Large} fontWeight="700" color={Colors.extra.black}>
              Data Upload Center
            </Text>
            <Text fontSize={Size.Small} color={Colors.extra.dark_gray}>
              Single source of truth for retinal scans and eye movement data from clinical devices.
            </Text>
          </HeaderLeft>
        </Header>

        <TabsContainer>
          <Tab active={activeTab === 'new'} onClick={() => setActiveTab('new')}>
            <TabIcon><UploadIconSvg /></TabIcon>
            New Upload Session
          </Tab>
          <Tab active={activeTab === 'recent'} onClick={() => setActiveTab('recent')}>
            <TabIcon><ClipboardIconSvg /></TabIcon>
            Recent Sessions
          </Tab>
          <Tab active={activeTab === 'device'} onClick={() => setActiveTab('device')}>
            <TabIcon><WifiIconSvg /></TabIcon>
            Device Integration
          </Tab>
        </TabsContainer>

        {activeTab === 'new' && (
          <FormGrid>
            {/* Session Information */}
            <FormCard>
              <CardHeader>
                <SessionInfoTitle>Session Information</SessionInfoTitle>
              </CardHeader>
              <CardSubtitle>Patient and device metadata.</CardSubtitle>

              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="patient-mrn">Patient MRN</Label>
                  <Input
                    id="patient-mrn"
                    type="text"
                    placeholder="MRN-YYYY-###"
                    value={formData.patientMRN}
                    onChange={(e) => handleInputChange('patientMRN', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="patient-name">Patient Name</Label>
                  <Input
                    id="patient-name"
                    type="text"
                    placeholder="First Last"
                    value={formData.patientName}
                    onChange={(e) => handleInputChange('patientName', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="session-date">Session Date</Label>
                  <Input
                    id="session-date"
                    type="date"
                    value={formData.sessionDate}
                    onChange={(e) => handleInputChange('sessionDate', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="device-type">Device Type</Label>
                  <Select
                    id="device-type"
                    value={formData.deviceType}
                    onChange={(e) => handleInputChange('deviceType', e.target.value)}
                  >
                    <option value="">Select clinical device</option>
                    {deviceTypes.map((device) => (
                      <option key={device.value} value={device.value}>
                        {device.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="session-notes">Session Notes</Label>
                  <TextArea
                    id="session-notes"
                    placeholder="Clinical notes, scan quality, patient conditions..."
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                  />
                </FormGroup>

                <SubmitButton type="submit" disabled={uploading}>
                  {uploading ? 'Creating...' : 'Create Upload Session'}
                </SubmitButton>
              </form>
            </FormCard>

            {/* File Upload */}
            <FormCard>
              <CardHeader>
                <FileUploadTitle>File Upload</FileUploadTitle>
              </CardHeader>
              <CardSubtitle>Upload retinal scans, eye movement data, and metadata files.</CardSubtitle>

              <UploadZone
                isDragOver={isDragOver}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <UploadIconWrapper><UploadIconSvg style={{ width: '48px', height: '48px' }} /></UploadIconWrapper>
                <UploadTitle>Upload Retinal Scans</UploadTitle>
                <UploadText>
                  Drag and drop your files here, or <BrowseLink>browse to choose</BrowseLink>
                </UploadText>
                <UploadFileInfo>
                  Supports: .dcm, .dicom, .jpg, .jpeg, .png, .tiff, .csv, .edf, .asc, .txt, .json • Max size: 100MB • Multiple files allowed
                </UploadFileInfo>
                <input
                  id="file-input"
                  type="file"
                  multiple
                  accept=".dcm,.dicom,.jpg,.jpeg,.png,.tiff,.csv,.edf,.asc,.txt,.json"
                  style={{ display: 'none' }}
                  onChange={handleFileInputChange}
                />
              </UploadZone>

              <SupportedTypes>
                <TypeTitle style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Supported File Types</TypeTitle>
                <TypeSection>
                  <TypeTitle>Retinal Scans:</TypeTitle>
                  <TypeList>
                    <li>DICOM files (.dcm, .dicom)</li>
                    <li>High-resolution images (.jpg, .png, .tiff)</li>
                    <li>OCT and fundus photography</li>
                  </TypeList>
                </TypeSection>
                <TypeSection>
                  <TypeTitle>Eye Movement Data:</TypeTitle>
                  <TypeList>
                    <li>EyeLink data files (.edf, .asc)</li>
                    <li>CSV formatted tracking data</li>
                    <li>Fixation and saccade measurements</li>
                  </TypeList>
                </TypeSection>
              </SupportedTypes>
            </FormCard>
          </FormGrid>
        )}

        {activeTab === 'recent' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <CardTitle>Recent Upload Sessions</CardTitle>
                <CardSubtitle>View and manage your recent upload sessions.</CardSubtitle>
              </div>
              <Badge>{mockUploadSessions.length} sessions</Badge>
                </div>

            <SessionsList>
              {mockUploadSessions.map((session) => (
                <SessionCard key={session.id}>
                  <SessionHeader>
                    <SessionHeaderLeft>
                      <SessionPatientName>{session.patientName} ({session.patientMRN})</SessionPatientName>
                      <SessionDetails>
                        Uploaded on {formatDate(session.sessionDate)} • {getDeviceLabel(session.deviceType)}
                      </SessionDetails>
                      {session.notes && (
                        <SessionNotes>{session.notes}</SessionNotes>
                      )}
                    </SessionHeaderLeft>
                    <SessionStatus status={session.status}>
                      {getStatusIcon(session.status)} {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                    </SessionStatus>
                  </SessionHeader>

                  <FilesSection>
                    <FilesTitle>Uploaded Files ({session.files.length})</FilesTitle>
                    <FilesList>
                      {session.files.map((file) => (
                        <FileItem key={file.id}>
                          <FileInfo>
                            <FileIcon>{getFileTypeIcon(file.type)}</FileIcon>
                            <FileDetails>
                              <FileName>{file.name}</FileName>
                              <FileMeta>
                                {formatFileSize(file.size)} • {file.eyeSide} • {file.type.replace('_', ' ')}
                              </FileMeta>
                            </FileDetails>
                          </FileInfo>
                          <FileActions>
                            {file.status === 'completed' && (
                              <FileStatusIcon status={file.status}><CheckIconSvg /></FileStatusIcon>
                            )}
                            {file.status === 'uploading' && (
                              <FileStatusIcon status={file.status}>⏳</FileStatusIcon>
                            )}
                            <ActionButton title="View">👁️</ActionButton>
                            <ActionButton title="Download">📥</ActionButton>
                            <ActionButton title="Delete">🗑️</ActionButton>
                          </FileActions>
                        </FileItem>
                      ))}
                    </FilesList>
                  </FilesSection>
                </SessionCard>
              ))}
            </SessionsList>
                  </div>
                )}

        {activeTab === 'device' && (
          <div>
            <DeviceIntegrationHeader>
              <HeaderLeft>
                <Text fontSize={Size.Large} fontWeight="700" color={Colors.extra.black}>
                  Device Integration Setup
                </Text>
                <Text fontSize={Size.Small} color={Colors.extra.dark_gray}>
                  Configure clinical devices for direct data integration.
                </Text>
              </HeaderLeft>
            </DeviceIntegrationHeader>

            <DevicesGrid>
              {deviceTypes.map((device) => (
                <DeviceCard key={device.value}>
                  <DeviceCardContent>
                    <DeviceIcon><CameraIconSvg /></DeviceIcon>
                    <DeviceInfo>
                      <DeviceName>{device.label}</DeviceName>
                      <DeviceType>Eye Tracker</DeviceType>
                    </DeviceInfo>
                    <ConfigureButton>Configure</ConfigureButton>
                  </DeviceCardContent>
                </DeviceCard>
              ))}
            </DevicesGrid>

            <DirectIntegrationSection>
              <DirectIntegrationIcon><CameraIconSvg /></DirectIntegrationIcon>
              <DirectIntegrationTitle>Direct Device Integration</DirectIntegrationTitle>
              <DirectIntegrationDescription>
                Connect clinical devices directly to the platform for seamless data transfer.
              </DirectIntegrationDescription>
              <SetupButton>Setup Device Connection</SetupButton>
            </DirectIntegrationSection>
          </div>
        )}
        </Container>
    </DashboardWrapper>
  );
};

export default Upload;
